import React, { useEffect, useId, useMemo, useState } from "react";
import { X } from "lucide-react";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Label } from "~/components/ui/label";
import { Switch } from "~/components/ui/switch";
import {
  SelectionCombobox,
  SelectionComboboxOption,
} from "~/components/ui/selection-combobox";
import { useLabIndicatorDefinitionsLazyQuery } from "generated/hooks";
import { useFetchAll } from "~/hooks/useFetchAll";
import {
  compareIndicatorDefinitions,
  compareIndicatorLabels,
  getIndicatorLabel,
} from "~/util/indicatorLabel";

export interface IndicatorLimitDefinition {
  id: string;
  name: string;
  displayName?: string | null;
}

export interface IndicatorLimitSaveInput {
  limitIndicators: boolean;
  allowedIndicatorDefinitionIds: string[];
}

export interface IndicatorLimitSectionProps {
  limitIndicators: boolean;
  allowedIndicatorDefinitions: IndicatorLimitDefinition[];
  /** Persists the limit; should throw (after toasting) on failure so the section stays dirty. */
  onSave: (input: IndicatorLimitSaveInput) => Promise<void>;
  disabled?: boolean;
}

const sortByLabel = compareIndicatorDefinitions;

const selectionKey = (limitIndicators: boolean, ids: readonly string[]) =>
  `${limitIndicators}:${[...ids].sort().join(",")}`;

/**
 * Lets PES staff restrict which indicators a customer's (non-staff) users can see.
 * Edits are local until "Save" is pressed so a half-finished selection is never persisted.
 */
export const IndicatorLimitSection: React.FC<IndicatorLimitSectionProps> = ({
  limitIndicators,
  allowedIndicatorDefinitions,
  onSave,
  disabled,
}) => {
  const [enabled, setEnabled] = useState(limitIndicators);
  const [selected, setSelected] = useState<IndicatorLimitDefinition[]>(
    [...allowedIndicatorDefinitions].sort(sortByLabel)
  );
  const [saving, setSaving] = useState(false);
  // Several customers render this section on one page, so the switch id must be unique.
  const switchId = useId();

  const persistedKey = selectionKey(
    limitIndicators,
    allowedIndicatorDefinitions.map((definition) => definition.id)
  );

  // Re-sync when the persisted customer changes (e.g. after a successful save or refetch).
  // Keyed on the persisted values rather than the array identity so an unrelated
  // parent re-render never discards in-progress edits.
  useEffect(() => {
    setEnabled(limitIndicators);
    setSelected([...allowedIndicatorDefinitions].sort(sortByLabel));
  }, [persistedKey]);

  const [fetchDefinitions] = useLabIndicatorDefinitionsLazyQuery();
  const { data: allDefinitions, loading: loadingDefinitions } = useFetchAll(
    fetchDefinitions,
    (d) => d.labIndicatorDefinitions,
    { variables: { after: null } }
  );

  const options = useMemo<SelectionComboboxOption[]>(() => {
    const selectedIds = new Set(selected.map((definition) => definition.id));
    return (allDefinitions ?? [])
      .filter(
        (definition): definition is NonNullable<typeof definition> =>
          Boolean(definition?.id) && !selectedIds.has(definition!.id)
      )
      .map((definition) => ({
        value: definition.id,
        label: getIndicatorLabel(definition),
      }))
      .sort((a, b) => compareIndicatorLabels(a.label, b.label));
  }, [allDefinitions, selected]);

  const selectedIds = selected.map((definition) => definition.id);
  const hasChanges = selectionKey(enabled, selectedIds) !== persistedKey;
  const needsSelection = enabled && selected.length === 0;
  const canSave = hasChanges && !needsSelection && !saving && !disabled;

  const handleAdd = (definitionId: string) => {
    const definition = (allDefinitions ?? []).find(
      (d) => d?.id === definitionId
    );
    if (!definition) {
      return;
    }
    setSelected((current) =>
      current.some((d) => d.id === definition.id)
        ? current
        : [...current, definition].sort(sortByLabel)
    );
  };

  const handleRemove = (definitionId: string) => {
    setSelected((current) => current.filter((d) => d.id !== definitionId));
  };

  const handleSave = async () => {
    if (!canSave) {
      return;
    }
    setSaving(true);
    try {
      await onSave({
        limitIndicators: enabled,
        allowedIndicatorDefinitionIds: selectedIds,
      });
    } catch {
      // The parent reports the failure; keep local edits so the user can retry.
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="space-y-4" data-testid="indicator-limit-section">
      <header className="space-y-1">
        <h3 className="text-sm font-semibold text-foreground">
          Indicator visibility
        </h3>
        <p className="text-sm text-muted-foreground">
          Restrict which indicators this customer&apos;s users can see on tests
          from its organisations, ERUs and cassettes. PES staff always see every
          indicator; CSV and PDF exports follow the limit.
        </p>
      </header>

      <div className="flex items-center justify-between gap-4 rounded-md border p-4">
        <Label htmlFor={switchId} className="cursor-pointer">
          Limit to the following indicators
        </Label>
        <Switch
          id={switchId}
          checked={enabled}
          onCheckedChange={(checked) => setEnabled(Boolean(checked))}
          disabled={disabled || saving}
        />
      </div>

      {enabled ? (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="w-full max-w-xs sm:max-w-sm">
            <SelectionCombobox
              placeholder={
                loadingDefinitions
                  ? "Loading indicators..."
                  : "Search and select indicator..."
              }
              options={options}
              emptyMessage={
                loadingDefinitions ? "Loading..." : "No more indicators"
              }
              onSelect={handleAdd}
              loading={loadingDefinitions}
              disabled={disabled || saving || loadingDefinitions}
            />
          </div>
          <div
            className="flex w-full flex-wrap gap-2"
            data-testid="indicator-limit-selected"
          >
            {selected.length > 0 ? (
              selected.map((definition) => (
                <Badge
                  key={definition.id}
                  variant="secondary"
                  className="inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-secondary/40 px-2.5 py-0.5 text-[11px] font-medium text-foreground"
                >
                  <span className="max-w-[200px] truncate">
                    {getIndicatorLabel(definition)}
                  </span>
                  <button
                    type="button"
                    className="rounded-full p-0.5 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    onClick={() => handleRemove(definition.id)}
                    aria-label={`Remove ${getIndicatorLabel(definition)}`}
                    disabled={disabled || saving}
                  >
                    <X className="size-3" aria-hidden="true" />
                  </button>
                </Badge>
              ))
            ) : (
              <p className="text-sm text-destructive">
                Select at least one indicator, or turn the limit off.
              </p>
            )}
          </div>
        </div>
      ) : null}

      <div className="flex items-center gap-3">
        <Button
          type="button"
          size="sm"
          onClick={handleSave}
          disabled={!canSave}
          data-testid="indicator-limit-save"
        >
          {saving ? "Saving..." : "Save indicator limit"}
        </Button>
        {hasChanges && !needsSelection ? (
          <span className="text-xs text-muted-foreground">Unsaved changes</span>
        ) : null}
      </div>
    </section>
  );
};

export default IndicatorLimitSection;
